import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import { Brand } from "@/components/brand";
import { StudioGuideModal, STUDIO_GUIDE_KEY } from "@/components/StudioGuide";

/**
 * StudioPage — hosts the Equal Design Studio (a self-contained build-out
 * visualization tool for Long Beach cannabis-equity applicants) inside the
 * authenticated Equal shell. The studio itself ships as a static asset at
 * /studio-app/index.html so its Leaflet + Three.js code stays isolated from the
 * React bundle; this page only provides the app header, role-aware navigation,
 * and the full-height frame.
 */

const STUDIO_SRC = "/studio-app/index.html";

const homeForRole = (role) => {
  if (role === "admin") return { path: "/admin", label: "Admin" };
  if (role === "city_official") return { path: "/city", label: "City Portal" };
  return { path: "/home", label: "Home" };
};

export default function StudioPage() {
  const navigate = useNavigate();
  const email = localStorage.getItem("email");
  const role = localStorage.getItem("role");
  const home = homeForRole(role);
  const [loaded, setLoaded] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Equal — Design Studio";
    if (!localStorage.getItem(STUDIO_GUIDE_KEY)) setTimeout(() => setShowGuide(true), 500);
    return () => { document.title = previousTitle; };
  }, []);

  const dismissGuide = () => { setShowGuide(false); localStorage.setItem(STUDIO_GUIDE_KEY, "1"); };

  return (
    <div className="h-screen flex flex-col bg-page" data-testid="studio-page">
      {/* Header — mirrors the Chat header so the studio reads as part of Equal */}
      <header className="flex items-center justify-between gap-4 px-5 md:px-7 h-14 bg-white border-b border-line">
        <Brand section="Design Studio" hint="Long Beach Cannabis Equity" />

        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button onClick={() => setShowGuide(true)}
            className="btn-ghost btn-sm text-equal-dark"
            data-testid="studio-help-button">
            <HelpCircle className="w-3.5 h-3.5" /> How it works
          </button>
          <button onClick={() => navigate(home.path)}
            className="btn-ghost btn-sm"
            data-testid="studio-back-button">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
            </svg>
            Back to {home.label}
          </button>
          <button onClick={() => navigate("/comps")}
            className="btn-ghost btn-sm"
            data-testid="studio-market-data-button">
            Market Data
          </button>
          <a href={STUDIO_SRC} target="_blank" rel="noopener noreferrer"
            className="btn-ghost btn-sm text-equal-dark"
            data-testid="studio-fullscreen-link">
            Open full screen
          </a>
          <div className="w-px h-4 bg-line" />
          <span className="hidden md:inline text-mute text-xs font-mono">{email}</span>
          <button onClick={() => { localStorage.clear(); navigate("/"); }}
            className="btn-quiet btn-sm"
            data-testid="studio-logout-button">
            Sign out
          </button>
        </div>
      </header>

      {/* Studio frame */}
      <div className="relative flex-1 min-h-0">
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center" data-testid="studio-loading">
            <div className="flex items-center gap-3 text-mute text-sm">
              <div className="w-5 h-5 border-2 border-line border-t-equal rounded-full animate-spin" />
              Loading Design Studio…
            </div>
          </div>
        )}
        <iframe
          title="Equal Design Studio"
          src={STUDIO_SRC}
          onLoad={() => setLoaded(true)}
          className="w-full h-full border-0 block"
          allow="camera; microphone; display-capture; clipboard-write"
          data-testid="studio-frame"
        />
      </div>

      {showGuide && <StudioGuideModal onClose={dismissGuide} />}
    </div>
  );
}
